import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Send, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import {
  getNewsletterRecipients,
  previewNewsletter,
  sendNewsletter,
} from "../../../config/api";

const NewsletterPreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const payload = location.state?.payload;

  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState("all");
  const [selectedSpecificIds, setSelectedSpecificIds] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    if (!payload) {
      navigate("/admin/newsletter", { replace: true });
      return;
    }
    previewNewsletter(payload)
      .then((res) => setPreviewHtml(res?.data?.html || res?.html || ""))
      .catch(() => toast.error("Failed to load preview"))
      .finally(() => setPreviewLoading(false));
  }, [payload, navigate]);

  useEffect(() => {
    getNewsletterRecipients()
      .then((res) =>
        setRecipients(res?.data?.recipients || res?.recipients || []),
      )
      .catch(() => toast.error("Failed to load recipients"))
      .finally(() => setRecipientsLoading(false));
  }, []);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds("all");
      setSelectedSpecificIds(new Set());
    } else {
      setSelectedIds("specific");
      setSelectedSpecificIds(new Set());
    }
  };

  const handleToggleRecipient = (id) => {
    setSelectedIds("specific");
    setSelectedSpecificIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const effectiveRecipientCount =
    selectedIds === "all" ? recipients.length : selectedSpecificIds.size;

  const handleSend = async () => {
    if (!payload?.subject) {
      toast.error("Subject is required");
      return;
    }
    if (effectiveRecipientCount === 0) {
      toast.error("Select at least one recipient");
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const recipientIds =
        selectedIds === "all" ? "all" : Array.from(selectedSpecificIds);
      const res = await sendNewsletter({
        ...payload,
        recipientIds,
      });
      const data = res?.data || res;
      setSendResult({
        sentCount: data.sentCount ?? data.sent ?? 0,
        failedCount: data.failedCount ?? data.failed ?? 0,
        errors: data.errors,
      });
      const sentCount = data.sentCount ?? data.sent ?? 0;
      const failedCount = data.failedCount ?? data.failed ?? 0;

      if (failedCount > 0) {
        toast.error(
          `Newsletter partially sent: ${sentCount} succeeded, ${failedCount} failed.`,
        );
      } else {
        toast.success(`Newsletter sent to ${sentCount} recipient(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send newsletter");
    } finally {
      setSending(false);
    }
  };

  if (!payload) return null;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate("/admin/newsletter")}
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to builder
        </button>
      </div>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Preview &amp; Send</h1>
        <p className="mt-1 text-sm text-gray-600">
          Subject: <strong>{payload.subject}</strong>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-900">
              Email preview
            </h2>
          </div>
          <div className="p-4 overflow-auto max-h-[600px]">
            {previewLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <iframe
                title="Newsletter preview"
                srcDoc={previewHtml}
                className="w-full min-h-[500px] border border-gray-200 rounded"
                sandbox="allow-same-origin"
              />
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              Recipients ({recipients.length} customers)
            </h2>
            {recipientsLoading ? (
              <p className="text-sm text-gray-500">Loading recipients…</p>
            ) : (
              <>
                <label className="flex items-center gap-2 mb-4">
                  <input
                    type="radio"
                    name="recipientMode"
                    checked={selectedIds === "all"}
                    onChange={() => handleSelectAll(true)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">All customers</span>
                </label>
                <label className="flex items-center gap-2 mb-4">
                  <input
                    type="radio"
                    name="recipientMode"
                    checked={selectedIds === "specific"}
                    onChange={() => handleSelectAll(false)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Select specific</span>
                </label>
                {selectedIds === "specific" && (
                  <ul className="max-h-48 overflow-auto border border-gray-200 rounded-lg divide-y divide-gray-200">
                    {recipients.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center gap-2 px-3 py-2"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSpecificIds.has(r.id)}
                          onChange={() => handleToggleRecipient(r.id)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-900 truncate flex-1">
                          {r.email}
                        </span>
                        <span className="text-xs text-gray-500 truncate max-w-[100px]">
                          {[r.first_name, r.last_name]
                            .filter(Boolean)
                            .join(" ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  Sending to <strong>{effectiveRecipientCount}</strong>{" "}
                  recipient(s)
                </p>
              </>
            )}
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            {sendResult ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  Sent: <strong>{sendResult.sentCount}</strong>
                  {sendResult.failedCount > 0 && (
                    <span className="text-amber-600 ml-2">
                      Failed: {sendResult.failedCount}
                    </span>
                  )}
                </p>
                {sendResult.errors?.length > 0 && (
                  <ul className="text-xs text-red-600 list-disc list-inside">
                    {sendResult.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>
                        {e.to}: {e.message}
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/admin/newsletter")}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Back to builder
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || effectiveRecipientCount === 0}
                className="inline-flex items-center w-full justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? "Sending…" : "Send newsletter"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPreview;
